import { apiClient } from './client'

// Types
export interface KnowledgeBaseItem {
  title: string
  article_count: number
  without_summary_count: number
}

export interface KnowledgeBasesResponse {
  knowledge_bases: KnowledgeBaseItem[]
}

export interface PreviewArticle {
  display_number: string
  number: string
  short_description: string
  knowledge_base: string
  language: string
  has_summary: boolean
  u_summary?: string | null
}

export interface ArticlePreviewResponse {
  articles: PreviewArticle[]
  total_articles: number
}

export interface AISummaryGenerateRequest {
  knowledge_base?: string
  article_ids?: string[]
  limit?: number
}

export interface JobSubmitResponse {
  job_id: string
  status: string
  message: string
  total_articles: number
}

export interface JobProgress {
  total_articles: number
  processed_articles: number
  successful_articles: number
  failed_articles: number
  skipped_articles: number
  percentage: number
}

export interface ArticleResult {
  display_number: string
  number: string
  short_description: string
  knowledge_base: string
  sys_id: string
  language: string
  status: string
  error?: string | null
  ai_summary?: string | null
}

export interface JobStatusResponse {
  job_id: string
  status: string  // submitted, processing, completed, failed
  knowledge_base?: string | null
  progress: JobProgress
  results: ArticleResult[]
  error_message?: string | null
}

export const aiSummaryApi = {
  // List available knowledge bases
  async getKnowledgeBases(): Promise<KnowledgeBasesResponse> {
    const response = await apiClient.get('/v1/ai-summary/knowledge-bases')
    return response.data
  },

  // Preview articles matching filters (no generation)
  async preview(params: {
    knowledge_base?: string
    article_ids?: string
    limit?: number
    summary_status?: string
  }): Promise<ArticlePreviewResponse> {
    const queryParams = new URLSearchParams()
    if (params.knowledge_base) queryParams.append('knowledge_base', params.knowledge_base)
    if (params.article_ids) queryParams.append('article_ids', params.article_ids)
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.summary_status) queryParams.append('summary_status', params.summary_status)

    const response = await apiClient.get(`/v1/ai-summary/preview?${queryParams}`)
    return response.data
  },

  // Submit AI summary generation job (returns immediately)
  async generate(request: AISummaryGenerateRequest): Promise<JobSubmitResponse> {
    const response = await apiClient.post('/v1/ai-summary/generate', request)
    return response.data
  },

  // Get job status (for polling)
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    const response = await apiClient.get(`/v1/ai-summary/jobs/${jobId}`)
    return response.data
  },

  // Download results as Excel
  async downloadExcel(jobId: string): Promise<void> {
    const response = await apiClient.get(`/v1/ai-summary/jobs/${jobId}/download`, {
      responseType: 'blob',
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    const contentDisposition = response.headers['content-disposition']
    const filenameMatch = contentDisposition?.match(/filename=(.+)/)
    const filename = filenameMatch ? filenameMatch[1] : 'ai_summary_results.xlsx'

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },
}

export default aiSummaryApi
